import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { Mail, MessageSquare, Stamp, Copy, Phone } from 'lucide-react';
import { useNotify } from 'react-admin';
import { useMessageDraft } from '../funerals/contexts/MessageDraftContext';
import { EmailComposeDialog, EmailComposeContext } from './EmailComposeDialog';
import PhysicalMailButton from '../funerals/PhysicalMailButton';
import { formatPhone } from '../lib/phone';

export interface ContactCardData {
  name: string;
  role?: string;
  phone?: string | null;
  email?: string | null;
  /** The person. Enables physical mail and comms-log attribution.
   *  Funeral clients and lead contacts used to be two separate ids here; since
   *  the contacts/clients merge they are the same row. */
  clientId?: number;
}

interface ContactCardProps {
  contact: ContactCardData;
  /** Owning entity for comms-log attribution + document lookup. */
  context: Omit<EmailComposeContext, 'clientId'>;
  /** Default CC for the email dialog (comma-separated). */
  ccDefault?: string;
  /** Render-prop for the edit control — call sites supply their own editor
   *  (dialog for funeral clients, inline form for lead contacts). */
  renderEdit?: () => React.ReactNode;
  /** Override the rendered Name value — used to make the name itself a clickable
   *  edit trigger. `contact.name` is still the source of truth for email/mail. */
  renderName?: () => React.ReactNode;
  /** Enable the physical-mail (PostGrid) action. Requires a funeral context and
   *  a clientId with a complete address. */
  enableMail?: boolean;
  /** Suppress the footer action row entirely (e.g. the deceased card). */
  hideActions?: boolean;
  /** Extra label/value detail rows (relationship, spouse, address, etc.). */
  children?: React.ReactNode;
  /** Extra classes on the card container (margins, etc.). */
  className?: string;
}

// Pretty-print North American numbers as +1 (XXX) XXX-XXXX. Non-NANP / unexpected
// shapes fall back to the original string so nothing is ever hidden.

// Matches the existing sidebar Row styling: label on the left, value on the right.
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-1.5">
      <span className="text-[13px] text-muted-foreground leading-5 shrink-0">{label}</span>
      <div className="text-[13px] text-foreground leading-5 min-w-0">{children}</div>
    </div>
  );
}

/** Shared size/treatment for footer action buttons. Exported so the edit
 *  render-prop at call sites can match the built-in actions exactly. */
export const actionBtnClass =
  'inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';

export function ContactCard({
  contact,
  context,
  ccDefault,
  renderEdit,
  renderName,
  enableMail,
  hideActions,
  children,
  className,
}: ContactCardProps) {
  const notify = useNotify();
  const { requestFocus } = useMessageDraft();
  const [emailOpen, setEmailOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [copyAnchor, setCopyAnchor] = useState<null | HTMLElement>(null);

  const emailContext: EmailComposeContext = {
    ...context,
    ...(contact.clientId != null ? { clientId: contact.clientId } : {}),
    ...(contact.clientId ? { clientId: contact.clientId } : {}),
  };

  const copy = (value: string, label: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => notify(`${label} copied`, { type: 'info' }))
      .catch(() => notify('Copy failed', { type: 'warning' }));
    setCopyAnchor(null);
  };

  const handleCopyClick = (e: React.MouseEvent<HTMLElement>) => {
    // One value → copy directly; both → open a small menu.
    if (contact.email && contact.phone) {
      setCopyAnchor(e.currentTarget);
    } else if (contact.email) {
      copy(contact.email, 'Email');
    } else if (contact.phone) {
      copy(contact.phone, 'Phone');
    }
  };

  const canMail = !!enableMail && context.funeralId != null && contact.clientId != null;
  const hasActions = !hideActions && !!(contact.phone || contact.email || canMail || renderEdit);

  return (
    <div className={`mx-1 rounded-lg border border-border bg-card ${className ?? ''}`}>
      {/* Detail rows — current label/value inline styling */}
      <div className="px-4 pt-3 pb-2">
        <Row label="Name">
          {renderName ? renderName() : <span className="font-medium">{contact.name || '—'}</span>}
        </Row>
        {contact.role && <Row label="Role">{contact.role}</Row>}
        {contact.phone && <Row label="Phone">{formatPhone(contact.phone)}</Row>}
        {contact.email && (
          <Row label="Email"><span className="truncate block">{contact.email}</span></Row>
        )}
        {children}
      </div>

      {/* Action icons — footer */}
      {hasActions && (
        <div className="px-3 py-2 border-t border-border flex items-center gap-1">
          {contact.phone && (
            <button
              type="button"
              className={actionBtnClass}
              title={`Call ${contact.phone}`}
              onClick={() => window.open(`aircall:${contact.phone}`, '_self')}
            >
              <img src="/aircall.png" alt="Call" className="w-4 h-4" />
            </button>
          )}
          {contact.phone && (
            <button
              type="button"
              className={actionBtnClass}
              title="Send SMS"
              onClick={() => requestFocus({ channel: 'sms', recipientPhone: contact.phone })}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          )}
          {contact.email && (
            <button type="button" className={actionBtnClass} title="Send email" onClick={() => setEmailOpen(true)}>
              <Mail className="w-4 h-4" />
            </button>
          )}
          {canMail && (
            <button type="button" className={actionBtnClass} title="Send physical mail" onClick={() => setMailOpen(true)}>
              <Stamp className="w-4 h-4" />
            </button>
          )}
          {(contact.email || contact.phone) && (
            <button type="button" className={actionBtnClass} title="Copy" onClick={handleCopyClick}>
              <Copy className="w-4 h-4" />
            </button>
          )}
          {renderEdit && <div className="ml-auto inline-flex items-center">{renderEdit()}</div>}
        </div>
      )}

      <Menu anchorEl={copyAnchor} open={!!copyAnchor} onClose={() => setCopyAnchor(null)}>
        {contact.email && (
          <MenuItem onClick={() => copy(contact.email!, 'Email')}>
            <Mail className="w-4 h-4 mr-2" /> Copy email
          </MenuItem>
        )}
        {contact.phone && (
          <MenuItem onClick={() => copy(contact.phone!, 'Phone')}>
            <Phone className="w-4 h-4 mr-2" /> Copy phone
          </MenuItem>
        )}
      </Menu>

      <EmailComposeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        recipient={{ name: contact.name, email: contact.email }}
        context={emailContext}
        ccDefault={ccDefault}
      />

      {canMail && (
        <PhysicalMailButton
          open={mailOpen}
          onClose={() => setMailOpen(false)}
          initialClientId={contact.clientId}
          hideTrigger
        />
      )}
    </div>
  );
}
